import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

interface BackgroundRippleProps {
  rows?: number;
  cols?: number;
  triggerDelay?: number; // ms after mount to auto-trigger
  onTrigger?: boolean; // External trigger
  rippleColor?: string;
  baseOpacity?: number;
  rippleOpacity?: number;
  animationDuration?: number;
  spreadDuration?: number; // How long for ripple to reach edges
}

interface CellState {
  row: number;
  col: number;
  delay: number;
  isActive: boolean;
}

export function BackgroundRipple({
  rows = 12,
  cols = 20,
  triggerDelay,
  onTrigger,
  rippleColor = 'rgba(16, 185, 129, 0.15)', // emerald-500
  baseOpacity = 0,
  rippleOpacity = 0.6,
  animationDuration = 400,
  spreadDuration = 800,
}: BackgroundRippleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cells, setCells] = useState<CellState[]>([]);
  const [rippleKey, setRippleKey] = useState(0);
  const isRipplingRef = useRef(false);

  // Create initial grid cells
  const initialCells = useMemo(() => {
    const newCells: CellState[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        newCells.push({ row: r, col: c, delay: 0, isActive: false });
      }
    }
    return newCells;
  }, [rows, cols]);

  useEffect(() => {
    setCells(initialCells);
  }, [initialCells]);

  // Trigger ripple effect from a point
  const triggerRipple = useCallback(
    (originX: number, originY: number) => {
      if (!containerRef.current || isRipplingRef.current) return;
      isRipplingRef.current = true;

      const rect = containerRef.current.getBoundingClientRect();
      const cellWidth = rect.width / cols;
      const cellHeight = rect.height / rows;

      // Calculate origin cell
      const originCol = Math.floor((originX - rect.left) / cellWidth);
      const originRow = Math.floor((originY - rect.top) / cellHeight);

      // Calculate delay for each cell based on distance from origin
      const maxDistance = Math.sqrt(rows * rows + cols * cols);

      const updatedCells = initialCells.map((cell) => {
        const distance = Math.sqrt(
          Math.pow(cell.col - originCol, 2) + Math.pow(cell.row - originRow, 2)
        );
        // Delay proportional to distance
        const delay = (distance / maxDistance) * spreadDuration;
        return { ...cell, delay, isActive: true };
      });

      setCells(updatedCells);
      setRippleKey((k) => k + 1);

      // Reset after full animation completes
      setTimeout(() => {
        setCells((prev) => prev.map((c) => ({ ...c, isActive: false })));
        isRipplingRef.current = false;
      }, spreadDuration + animationDuration + 100);
    },
    [initialCells, cols, rows, spreadDuration, animationDuration]
  );

  // Trigger from center
  const triggerFromCenter = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      triggerRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  }, [triggerRipple]);

  // Auto-trigger after delay
  useEffect(() => {
    if (triggerDelay !== undefined && triggerDelay >= 0) {
      const timer = setTimeout(triggerFromCenter, triggerDelay);
      return () => clearTimeout(timer);
    }
  }, [triggerDelay, triggerFromCenter]);

  // External trigger (only trigger on true transition)
  const prevTriggerRef = useRef(false);
  useEffect(() => {
    if (onTrigger && !prevTriggerRef.current) {
      triggerFromCenter();
    }
    prevTriggerRef.current = onTrigger ?? false;
  }, [onTrigger, triggerFromCenter]);

  // Handle click
  const handleClick = (e: React.MouseEvent) => {
    triggerRipple(e.clientX, e.clientY);
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden pointer-events-auto cursor-pointer z-[1]"
      onClick={handleClick}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
      }}
    >
      {/* CSS Keyframes for pulse animation */}
      <style>{`
        @keyframes ripplePulse {
          0% { opacity: ${baseOpacity}; }
          30% { opacity: ${rippleOpacity}; }
          100% { opacity: ${baseOpacity}; }
        }
      `}</style>

      {cells.map((cell, index) => (
        <div key={index} className="relative">
          {/* Ripple cell */}
          <div
            key={`${index}-${rippleKey}`}
            className="absolute inset-0"
            style={{
              backgroundColor: rippleColor,
              opacity: baseOpacity,
              animation: cell.isActive
                ? `ripplePulse ${animationDuration}ms ease-out ${cell.delay}ms forwards`
                : 'none',
            }}
          />
          {/* No visible grid lines - pure ripple effect */}
        </div>
      ))}
    </div>
  );
}
